import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Globe, Mail, Upload, FileText, Image, X, Sparkles, MessageSquare, Pencil, ArrowRight, Trash2, Loader2, Eye, Brain } from "lucide-react";
import { getRiskBadge, API_URL } from "./helpers";
import { useState } from "react";

export const OpportunityDetailModal = ({
  isOpen, onOpenChange, detailOpp, detailFiles, uploading,
  onFileUpload, onFileDelete, advisorLoading, advisorResult,
  onAskAdvisor, onEdit, onConvertToAsset, onDelete, isDemo,
}) => {
  const [analyzingFile, setAnalyzingFile] = useState(null);
  const [fileAnalysis, setFileAnalysis] = useState({});

  const analyzeFile = async (file) => {
    if (!detailOpp || isDemo) return;
    setAnalyzingFile(file.file_id);
    try {
      const resp = await fetch(`${API_URL}/api/opportunities/${detailOpp.opportunity_id}/files/${file.file_id}/analyze`, {
        method: "POST",
        credentials: "include",
      });
      if (!resp.ok) throw new Error("Analysis failed");
      const data = await resp.json();
      setFileAnalysis(prev => ({ ...prev, [file.file_id]: data.analysis }));
    } catch (err) {
      console.error("File analysis error:", err);
    } finally {
      setAnalyzingFile(null);
    }
  };

  if (!detailOpp) return null;
  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-['Barlow_Condensed'] uppercase tracking-wider flex items-center gap-2">
            {detailOpp.name}
            <Badge variant="secondary" className="text-[9px] normal-case">{detailOpp.category}</Badge>
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          {detailOpp.image_url && (
            <div className="h-40 rounded-lg overflow-hidden bg-secondary/30">
              <img src={detailOpp.image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          {detailOpp.description && <p className="text-sm text-muted-foreground">{detailOpp.description}</p>}

          {/* Key Metrics */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {detailOpp.price && (<div className="p-3 bg-secondary/40 rounded-lg"><p className="text-[10px] uppercase text-muted-foreground tracking-wider">Price</p><p className="font-mono font-bold text-sm">{detailOpp.price}</p></div>)}
            {detailOpp.expected_return && (<div className="p-3 bg-secondary/40 rounded-lg"><p className="text-[10px] uppercase text-muted-foreground tracking-wider">Expected Return</p><p className="font-mono font-bold text-sm text-green-500">{detailOpp.expected_return}</p></div>)}
            {detailOpp.apy > 0 && (<div className="p-3 bg-secondary/40 rounded-lg"><p className="text-[10px] uppercase text-muted-foreground tracking-wider">APY</p><p className="font-mono font-bold text-sm">{detailOpp.apy}%</p></div>)}
            {detailOpp.tvl > 0 && (<div className="p-3 bg-secondary/40 rounded-lg"><p className="text-[10px] uppercase text-muted-foreground tracking-wider">TVL</p><p className="font-mono font-bold text-sm">${Number(detailOpp.tvl).toLocaleString()}</p></div>)}
            {detailOpp.ticket && (<div className="p-3 bg-secondary/40 rounded-lg"><p className="text-[10px] uppercase text-muted-foreground tracking-wider">Min Ticket</p><p className="font-mono font-bold text-sm">{detailOpp.ticket}</p></div>)}
            {detailOpp.protocol && (<div className="p-3 bg-secondary/40 rounded-lg"><p className="text-[10px] uppercase text-muted-foreground tracking-wider">Protocol</p><p className="font-bold text-sm">{detailOpp.protocol}</p></div>)}
            {detailOpp.chain && (<div className="p-3 bg-secondary/40 rounded-lg"><p className="text-[10px] uppercase text-muted-foreground tracking-wider">Chain</p><p className="font-bold text-sm">{detailOpp.chain}</p></div>)}
            <div className="p-3 bg-secondary/40 rounded-lg"><p className="text-[10px] uppercase text-muted-foreground tracking-wider">Risk</p><div className="mt-0.5">{getRiskBadge(detailOpp.risk_score)}</div></div>
          </div>

          {detailOpp.notes && (<div><p className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Notes</p><p className="text-sm bg-secondary/30 p-3 rounded-lg">{detailOpp.notes}</p></div>)}
          {detailOpp.risk_notes && (<div><p className="text-[10px] uppercase text-muted-foreground tracking-wider mb-1">Risk Notes</p><p className="text-sm bg-secondary/30 p-3 rounded-lg">{detailOpp.risk_notes}</p></div>)}

          <div className="flex gap-2 flex-wrap">
            {detailOpp.url && (<Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => window.open(detailOpp.url, "_blank")}><Globe className="w-3 h-3" /> Website</Button>)}
            {detailOpp.contact && (<Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => window.open(detailOpp.contact.includes("@") ? `mailto:${detailOpp.contact}` : detailOpp.contact, "_blank")}><Mail className="w-3 h-3" /> Contact</Button>)}
          </div>

          {/* Files */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider">Documents & Images</p>
              {!isDemo && (
                <label className="cursor-pointer">
                  <input type="file" className="hidden" accept="image/*,.pdf" onChange={onFileUpload} disabled={uploading} />
                  <Button variant="outline" size="sm" className="text-xs gap-1 pointer-events-none" disabled={uploading}>
                    {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />} Upload
                  </Button>
                </label>
              )}
            </div>
            {detailFiles.length > 0 ? (
              <div className="space-y-3">
                {detailFiles.map((f) => (
                  <div key={f.file_id} className="p-3 bg-secondary/30 rounded-lg border border-border hover:border-primary/40 transition-colors">
                    <div className="flex items-center justify-between">
                      <a href={`${API_URL}/api/opportunities/${detailOpp.opportunity_id}/files/${f.file_id}`} target="_blank" rel="noreferrer" className="flex items-center gap-2 flex-1 min-w-0">
                        {f.content_type?.startsWith("image/") ? <Image className="w-4 h-4 text-blue-400 shrink-0" /> : <FileText className="w-4 h-4 text-orange-400 shrink-0" />}
                        <span className="text-sm truncate">{f.filename}</span>
                        <Eye className="w-3 h-3 text-muted-foreground" />
                      </a>
                      <div className="flex items-center gap-1 ml-2">
                        {!isDemo && f.content_type?.startsWith("image/") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs gap-1 text-primary hover:text-primary"
                            onClick={() => analyzeFile(f)}
                            disabled={analyzingFile === f.file_id}
                            data-testid={`analyze-file-${f.file_id}`}
                          >
                            {analyzingFile === f.file_id ? (
                              <Loader2 className="w-3 h-3 animate-spin" />
                            ) : (
                              <Brain className="w-3 h-3" />
                            )}
                            {analyzingFile === f.file_id ? "Analyzing..." : "Analyze"}
                          </Button>
                        )}
                        {!isDemo && (
                          <button
                            className="p-1 rounded hover:bg-destructive/20 text-destructive transition-colors"
                            onClick={() => onFileDelete(f.file_id)}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* File Analysis Result */}
                    {fileAnalysis[f.file_id] && (
                      <div className="mt-3 pt-3 border-t border-border">
                        <div className="flex items-center gap-1.5 mb-2">
                          <Brain className="w-3.5 h-3.5 text-primary" />
                          <span className="text-[10px] uppercase text-primary font-bold tracking-wider">AI Analysis</span>
                        </div>
                        <div className="text-xs text-muted-foreground whitespace-pre-wrap leading-relaxed bg-secondary/50 p-3 rounded-md max-h-64 overflow-y-auto">
                          {fileAnalysis[f.file_id]}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (<p className="text-xs text-muted-foreground italic">No files attached. Upload documents for AI analysis.</p>)}
          </div>

          {/* AI Advisor */}
          <div className="border-t border-border pt-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] uppercase text-muted-foreground tracking-wider flex items-center gap-1"><Sparkles className="w-3 h-3" /> AI Advisor Opinion</p>
              <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => onAskAdvisor(detailOpp)} disabled={advisorLoading || isDemo} data-testid="ask-advisor-btn">
                {advisorLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <MessageSquare className="w-3 h-3" />}
                {advisorLoading ? "Analyzing..." : "Ask Advisor"}
              </Button>
            </div>
            {advisorResult && (<div className="bg-secondary/30 p-4 rounded-lg text-sm whitespace-pre-wrap leading-relaxed" data-testid="advisor-result">{advisorResult}</div>)}
          </div>

          {/* Actions */}
          <div className="flex gap-2 pt-2 border-t border-border">
            <Button variant="outline" size="sm" className="text-xs gap-1" onClick={() => onEdit(detailOpp)} disabled={isDemo}><Pencil className="w-3 h-3" /> Edit</Button>
            <Button variant="default" size="sm" className="text-xs gap-1 bg-primary text-primary-foreground" onClick={() => onConvertToAsset(detailOpp)} disabled={isDemo} data-testid="convert-to-asset-btn"><ArrowRight className="w-3 h-3" /> Convert to Asset</Button>
            <Button variant="ghost" size="sm" className="text-xs gap-1 ml-auto text-destructive" onClick={() => onDelete(detailOpp)} disabled={isDemo}><Trash2 className="w-3 h-3" /> Delete</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
